import type {
  InventoryFilterOptions,
  PackageResponse
} from "@/api/useInventory.types"
import { getExploitabilityByKeys } from "@/const/insights-constants"
import { EXPLOITABILITY_FILTER_OPTIONS } from "@/const/options.constants"
import { defaultSortResourcesByType } from "@/domain/resource"
import { getIssuesCountMapOrderedSeverity } from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import * as filtersHelper from "@/helpers/filters.helper"
import {
  formatLabelWithQuotesIfNeeded,
  getShouldFetchFilterOption
} from "@/helpers/filters.helper"
import { getLastDetectedCellData } from "@/helpers/table.helpers"
import { TFunction } from "@/hooks/useTranslation"
import { Filters } from "@/organisms/SmartSearch"
import { FilterServiceNS } from "@/organisms/SmartSearch/service/FilterService/FilterService.types"
import { getApplicationsMetaData } from "@/pages/TriagePage/application/useTriage.selector"
import { InventoryFilters } from "../../utils/filters.utils"

export const getDataProject = (data: PackageResponse["data"]) =>
  (data || []).map((record: any, index: number) => {
    const { id, data } = record || {}
    const { vulnerabilitiesSummary, insights, resources, ...item } = data

    return {
      id,
      key: id,
      applicationsCount: getApplicationsMetaData(
        item.relatedApplications || []
      ),
      license: {
        id: `${id}_${index}`,
        count: item.licenses.length - 1,
        items: item.licenses,
        label: item.licenses[0] || ""
      },
      issuesSummary: getIssuesCountMapOrderedSeverity(vulnerabilitiesSummary),
      insights: {
        ...getExploitabilityByKeys(insights.exploitMaturity),
        ...insights,
        id: `${id}_insights`
      },
      lastSeenDate: getDateInUserLocale(
        getDateFromSeconds(item.lastSeen),
        DEFAULT_LONG_FORMAT
      ),
      projects: item.projectIds.length,
      expandable: !!item.projectIds.length,
      resources: defaultSortResourcesByType(resources),
      ...item,
      ...getLastDetectedCellData(record)
    }
  })

export const getPackages = (response: PackageResponse) => {
  const { data = [], ...rest } = response

  return {
    data: data.map((record: any, index: number) => {
      const { id, data } = record || {}
      const { vulnerabilitiesSummary, insights, resources, ...item } = data

      return {
        id,
        key: id,
        applicationsCount: getApplicationsMetaData(
          item.relatedApplications || []
        ),
        license: {
          id: `${item.key}_${index}`,
          count: item.licenses.length - 1,
          items: item.licenses,
          label: item.licenses[0] || ""
        },
        issuesSummary: getIssuesCountMapOrderedSeverity(vulnerabilitiesSummary),
        insights: {
          ...getExploitabilityByKeys(insights.exploitMaturity),
          ...insights,
          id
        },
        lastSeenDate: getDateInUserLocale(
          getDateFromSeconds(item.lastSeen),
          DEFAULT_LONG_FORMAT
        ),
        projects: item.projectIds.length,
        expandable: !!item.projectIds.length,
        resources: defaultSortResourcesByType(resources),
        ...item,
        ...getLastDetectedCellData(record)
      }
    }),
    ...rest
  }
}

export const getProjectsByPackage = (response: {
  pages: PackageResponse[]
}) => {
  const { pages, ...rest } = response

  return { pages: getDataProject(pages.flatMap((page) => page.data)), ...rest }
}

type PropsFilter = { t: TFunction }

export const getFilters = ({ t }: PropsFilter): Filters => {
  const keyValues = "filters.helpers.multipleValues"
  return [
    filtersHelper.getSeverityFilter(t),
    {
      key: "packageLanguage",
      label: t("filters.languages"),
      type: "multiple",
      options: [getShouldFetchFilterOption()],
      helper: [
        {
          label: t(keyValues, { label: t("filters.languages") }),
          description: "Equals value1 OR value2"
        }
      ]
    },
    filtersHelper.getPackageLicenseFilter(t),
    filtersHelper.getPackageVersionFilter(t),
    filtersHelper.getPackageNameFilter(t),
    filtersHelper.getBaseImageNameFilter(t),
    filtersHelper.getBaseImageDigestFilter(t),
    {
      key: "isVulnerable",
      label: t("filters.vulnerable"),
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" }
      ]
    },
    filtersHelper.getVulnerabilityIdFilter(t),
    filtersHelper.getCveIdFilter(t),
    filtersHelper.getHighProfileFilter(t),
    filtersHelper.getCweIdFilter(t, "multiple"),
    {
      key: "cweDescription",
      label: t("filters.cweName").toLowerCase(),
      displayLabel: t("filters.cweName"),
      type: "input",
      helper: [
        {
          label: t(keyValues, { label: t("filters.cweName") }),
          description: 'Matches multiple cwe name (e.g. "Prototype Pollution")'
        }
      ]
    },
    {
      key: "packageExploitability",
      label: t("filters.exploitability"),
      type: "multiple",
      options: EXPLOITABILITY_FILTER_OPTIONS
    },
    {
      key: "isPackageRuntime",
      label: t("filters.runtime"),
      type: "single",
      order: 1,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO", disabled: true },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ],
      helper: [{ description: "Packages that are seen in runtime" }],
      replaceWith: "runtime"
    },
    {
      key: "isPackageIngress",
      label: t("filters.ingress"),
      type: "single",
      order: 3,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isPackageDirect",
      label: t("filters.isDirect"),
      order: 4,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isPackageInDirect",
      label: t("filters.isIndirect"),
      order: 5,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isPackageFromBaseImage",
      label: t("filters.fromBaseImage"),
      order: 6,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isPackageFromThirdParty",
      label: t("filters.fromThirdParty"),
      order: 7,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isPackageDevDependency",
      label: t("filters.isDevDependency"),
      type: "single",
      order: "none",
      options: [
        { label: "No", value: "NO" },
        { label: "Yes", value: "YES" }
      ],
      helper: [{ description: t("filters.helpers.devDependencySingle") }]
    },
    InventoryFilters.application(t),
    InventoryFilters.repositoryName(t),
    InventoryFilters.imageName(t),
    InventoryFilters.namespace(t),
    InventoryFilters.environment(t),
    filtersHelper.getPackageCategoryAI(t),
    filtersHelper.getIsPackageAiRelated(t)
  ]
}

/**
 * Transforms API filter options to the format expected by FilterService.updateFiltersOptions
 */
export const transformApiOptionsToFilterServiceFormat = (response: {
  data: InventoryFilterOptions
}): Record<string, FilterServiceNS.Options[]> => {
  if (!response.data) return {}

  const result: Record<string, FilterServiceNS.Options[]> = {}

  for (const [key, values] of Object.entries(response.data)) {
    if (Array.isArray(values) && values.length > 0) {
      result[key] = values.map((value) => ({
        label: formatLabelWithQuotesIfNeeded(value),
        value: value
      }))
    }
  }

  return result
}
